import type { Duration, DurationUnit } from 'date-fns'

import { EventStatus } from '@setplex/tria-api'
import {
  add,
  differenceInCalendarDays,
  differenceInDays,
  format,
  formatDistanceToNowStrict,
  formatDuration,
  getDefaultOptions,
} from 'date-fns'
import { enUS } from 'date-fns/locale'
import { t } from '~/core/i18n'
import {
  ONE_DAY_MS,
  ONE_HOUR_MS,
  ONE_MINUTE_MS,
  ONE_SECOND_MS,
} from '~/shared/constants/time'
import { HAS_ISO8601_TIMEZONE_RE } from '../constants/regularExp'
import { formatTimeString } from './strings'

export const DATE_FORMATS = {
  // 15:19 // 24h
  HH_MM_24: 'HH:mm',
  // 08:15 // 12h
  HH_MM: 'hh:mm',
  // 8:15 AM/PM localized (legacy; prefer formatTimeUnified)
  HH_MM_LOCALIZED: 'p',
  // Aug 29 (legacy; prefer DD_MMM)
  MMM_DD: 'MMM dd',
  // 25 Jan
  DD_MMM: 'd MMM',
  // 25 Jan 2024
  DD_MMM_YYYY: 'd MMM yyyy',
  // 2024
  YYYY: 'yyyy',
  // 2024-01-26
  YYYY_MM_DD: 'yyyy-MM-dd',
  // 2024/01/26
  YYYY_MM_DD_JA: 'yyyy/MM/dd',
  // 11-29-17
  HH_MM_SS: 'HH-mm-ss',
  // 11:29:17
  'HH:MM:SS': 'HH:mm:ss',
  // Apr 29, 1453, 12:00 AM/PM localized (legacy; prefer formatDateTimeWithYearUnified)
  MMM_DD_YYYY_HH_MM_LOCALIZED: 'PPp',
  // 24 Jan 12:00 AM/PM localized (legacy; prefer formatDateTimeUnified)
  DD_MMM_HH_MM_LOCALIZED: 'd MMM p',
  // 2024/01/26 12:00 AM/PM localized
  YYYY_MM_DD_HH_MM_LOCALIZED_JA: 'yyyy/MM/dd p',
  // Apr 29, 1453 localized (legacy; prefer DD_MMM_YYYY)
  MMM_DD_YYYY_LOCALIZED: 'PP',
  // Tue, 24 Jan
  EEE_DD_MMM: 'EEE, d MMM',
  // Tue, 2024/01/26
  EEE_DD_MMM_JA: 'EEE, yyyy/MM/dd',
} as const
export type DATE_FORMATS = (typeof DATE_FORMATS)[keyof typeof DATE_FORMATS]

interface FormatDateDurationParams {
  duration: Duration
  format?: DurationUnit[]
  short?: boolean
}

export const asDate = (date: number | string | Date): Date => {
  return typeof date === 'number' || typeof date === 'string'
    ? new Date(date)
    : date
}

export const asYear = (date: number | Date): Date => {
  return typeof date === 'number' ? new Date(date, 0) : date
}

export const isJapaneseDateLocale = (): boolean => {
  const defaultOptions = getDefaultOptions()
  const locale = defaultOptions.locale
  return Boolean(locale?.code?.includes('ja'))
}

export const isHour12Locale = (): boolean => {
  const { locale } = getDefaultOptions()
  const pattern = locale?.formatLong?.time({ width: 'short' }) ?? ''
  return /[abB]/.test(pattern)
}

export const swapDateFormat = (dateFormat: string) => {
  if (!isJapaneseDateLocale()) {
    return dateFormat
  }

  switch (dateFormat) {
    case DATE_FORMATS.DD_MMM:
    case DATE_FORMATS.DD_MMM_YYYY:
    case DATE_FORMATS.YYYY_MM_DD:
      return DATE_FORMATS.YYYY_MM_DD_JA
    case DATE_FORMATS.DD_MMM_HH_MM_LOCALIZED:
      return DATE_FORMATS.YYYY_MM_DD_HH_MM_LOCALIZED_JA
    case DATE_FORMATS.EEE_DD_MMM:
      return DATE_FORMATS.EEE_DD_MMM_JA
    default:
      return dateFormat
  }
}

export const formatDate: typeof format = (date, dateFormat, options) => {
  return format(date, swapDateFormat(dateFormat), options)
}

export const formatTimeUnified = (date: Date | number | string): string => {
  const d = asDate(date)
  if (isHour12Locale()) {
    const time = format(d, 'h:mm')
    const period = format(d, 'a', { locale: enUS })
    return `${time} ${period}`
  }
  return format(d, 'HH:mm')
}

export const formatDateTimeUnified = (date: Date | number | string): string => {
  const d = asDate(date)
  return `${formatDate(d, DATE_FORMATS.DD_MMM)}, ${formatTimeUnified(d)}`
}

export const formatDateTimeWithYearUnified = (
  date: Date | number | string
): string => {
  const d = asDate(date)
  return `${formatDate(d, DATE_FORMATS.DD_MMM_YYYY)}, ${formatTimeUnified(d)}`
}

const getTimeOfDayMinutes = (date: Date): number =>
  date.getHours() * 60 + date.getMinutes()

/** Overnight program: ends next calendar day at an earlier clock time than start. */
export const isOvernightMidnightCrossing = (
  start: Date | number | string,
  end: Date | number | string
): boolean => {
  const s = asDate(start)
  const e = asDate(end)
  return (
    differenceInCalendarDays(e, s) === 1 &&
    getTimeOfDayMinutes(e) < getTimeOfDayMinutes(s)
  )
}

type FormatTimeRangeUnifiedOptions = {
  /** When true, same-day / midnight-crossing ranges include the start date once. */
  alwaysIncludeDate?: boolean
}

export const formatTimeRangeUnified = (
  start: Date | number | string,
  end: Date | number | string,
  options?: FormatTimeRangeUnifiedOptions
): string => {
  const s = asDate(start)
  const e = asDate(end)
  const dayDiff = differenceInCalendarDays(e, s)

  // same day OR overnight midnight crossing → time only (unless date is required)
  if (dayDiff === 0 || isOvernightMidnightCrossing(s, e)) {
    const overnight = isOvernightMidnightCrossing(s, e)
    return t('program-start-end-time', {
      lng: 'date',
      startDate: options?.alwaysIncludeDate
        ? formatDateTimeUnified(s)
        : formatTimeUnified(s),
      endDate:
        options?.alwaysIncludeDate && overnight
          ? formatDateTimeUnified(e)
          : formatTimeUnified(e),
    })
  }

  const sameYear = s.getFullYear() === e.getFullYear()
  const startLabel = sameYear
    ? formatDateTimeUnified(s)
    : formatDateTimeWithYearUnified(s)
  const endLabel = sameYear
    ? formatDateTimeUnified(e)
    : formatDateTimeWithYearUnified(e)

  return t('program-start-end-time', {
    lng: 'date',
    startDate: startLabel,
    endDate: endLabel,
  })
}

// '2 years 9 months 1 week 7 days 5 hours 9 minutes 30 seconds'
// short: '2y 9m 1w 7d 5h 9m 30s'
export const formatDateDuration = ({
  duration,
  format,
  short,
}: FormatDateDurationParams) => {
  const durationString = formatDuration(duration, { format })
  const shortDurationString = formatTimeString(durationString)

  return short ? shortDurationString : durationString
}

interface FormatDateDistanceToNowParams {
  minutes: number
  hours: number
}
// in 1 hour | in 7 minutes | in 1 hour 7 minutes
export const formatDateDistanceToNow = ({
  minutes,
  hours,
}: FormatDateDistanceToNowParams) => {
  const currentDate = new Date()
  const untilDateMinutes = add(currentDate, {
    minutes,
  })
  const untilDateHours = add(currentDate, {
    hours,
  })
  const distanceHoursSuffix = formatDistanceToNowStrict(untilDateHours, {
    addSuffix: true,
  })

  const distanceMinutes = formatDistanceToNowStrict(untilDateMinutes)
  const distanceMinutesSuffix = formatDistanceToNowStrict(untilDateMinutes, {
    addSuffix: true,
  })

  if (hours && !minutes) {
    return distanceHoursSuffix
  }

  if (!hours && minutes) {
    return distanceMinutesSuffix
  }

  return `${distanceHoursSuffix} ${distanceMinutes}`
}

// in 1 hour | in 7 minutes | in 1 hour 7 minutes | in 1 hour 7 minutes 10 seconds
export const formatSecondsDistanceToNow = ({
  minutes,
  hours,
}: FormatDateDistanceToNowParams) => {
  const currentDate = new Date()
  const untilDateMinutes = add(currentDate, {
    minutes,
  })
  const untilDateHours = add(currentDate, {
    hours,
  })
  const distanceHoursSuffix = formatDistanceToNowStrict(untilDateHours, {
    addSuffix: true,
  })

  const distanceMinutes = formatDistanceToNowStrict(untilDateMinutes)
  const distanceMinutesSuffix = formatDistanceToNowStrict(untilDateMinutes, {
    addSuffix: true,
  })

  if (hours && !minutes) {
    return distanceHoursSuffix
  }

  if (!hours && minutes) {
    return distanceMinutesSuffix
  }

  return `${distanceHoursSuffix} ${distanceMinutes}`
}

export const formatYearToLocaleString = (year: number): string => {
  const date = formatDate(asYear(year), DATE_FORMATS.YYYY)
  return t('date-formatted', { lng: 'date', date })
}

export const formatStartTime = (
  time: string | Date | number,
  formatTemplate: string
): string => {
  const d = asDate(time)
  let date: string
  if (formatTemplate === DATE_FORMATS.HH_MM_LOCALIZED) {
    date = formatTimeUnified(d)
  } else if (formatTemplate === DATE_FORMATS.DD_MMM_HH_MM_LOCALIZED) {
    date = formatDateTimeUnified(d)
  } else if (formatTemplate === DATE_FORMATS.MMM_DD_YYYY_HH_MM_LOCALIZED) {
    date = formatDateTimeWithYearUnified(d)
  } else {
    date = formatDate(d, formatTemplate)
  }
  return t('date-formatted', { lng: 'date', date })
}
/**
 * Function formats date according next rules:
 * 0 < N <= 24 – 1 day
 * 24 < N <= 48 – 2 days
 * @param remainingTimeInMillis
 * @param short - abbreviated form for corner labels, which have no room for
 * the spelled-out unit
 */
export const getFormattedDate = (
  remainingTimeInMillis: number,
  short = false
) => {
  // +1 because full and partial days are needed. Cant use here formatDistanceStrict, because sometime days > 29
  const days = differenceInDays(new Date(remainingTimeInMillis), new Date()) + 1

  return t(short ? 'days_short' : 'day_count', { count: days })
}

export const formatEpgSubtitle = (
  status: EventStatus | undefined,
  liveEventStart: string,
  liveEventEnd: string
) => {
  if (status === EventStatus.LIVE || status === EventStatus.ENDED) {
    return formatTimeRangeUnified(liveEventStart, liveEventEnd, {
      alwaysIncludeDate: status === EventStatus.ENDED,
    })
  }

  return ''
}

// This helper is needed only for tests running on server side
// Related with NARROW NO-BREAK SPACE (U+202f) issue in Node (https://github.com/nodejs/node/issues?q=is%3Aissue+U%2B202f+is%3Aclosed)
export const formatDateAnswer = (string: string): string =>
  string.replace(/[\u202F\u00A0]/, ' ')

/**
 * Set seconds to 0 in date or timestamp (`now` by default)
 */
export const zeroizeSeconds = (from: Date | number = Date.now()): number =>
  Math.trunc(Number(from) / ONE_MINUTE_MS) * ONE_MINUTE_MS

/**
 * Calculate time units within two dates or timestamps, or single time range
 * used for formatting time left
 */
export const timeWithin = (from: Date | number, to?: Date | number) => {
  const diff = Math.abs(to == null ? Number(from) : Number(to) - Number(from))
  const days = Math.floor(diff / ONE_DAY_MS)
  const hours = Math.floor((diff % ONE_DAY_MS) / ONE_HOUR_MS)
  const minutes = Math.floor((diff % ONE_HOUR_MS) / ONE_MINUTE_MS)
  const seconds = Math.floor((diff % ONE_MINUTE_MS) / ONE_SECOND_MS)
  return { days, hours, minutes, seconds }
}

export const formatExpirationDate = (expirationDate?: string) => {
  if (!expirationDate) return ''

  const expirationTimeZ = hasISO8601TimezoneDefined(expirationDate)
    ? expirationDate
    : `${expirationDate}Z`

  return formatDate(new Date(expirationTimeZ), DATE_FORMATS.DD_MMM_YYYY)
}

export function hasISO8601TimezoneDefined(date: string): boolean {
  return HAS_ISO8601_TIMEZONE_RE.test(date)
}
